'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Loader2, CheckCircle2, ArrowRight } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

import { submitLead } from '@/app/actions/leads'
import { useTranslations } from 'next-intl'

// Schema for the form
const formSchema = z.object({
  fullName: z.string().min(2, 'Name is required'),
  email: z.string().email('Invalid email address'),
  linkedinUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  orgName: z.string().min(2, 'Organization name is required'),
  role: z.string().min(2, 'Role is required'),
  networkSize: z.string().optional(),
})

type FormData = z.infer<typeof formSchema>

export function LeadQualificationForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const t = useTranslations('landing')

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: '',
      email: '',
      linkedinUrl: '',
      orgName: '',
      role: '',
      networkSize: '',
    },
  })

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)

    try {
      const result = await submitLead(data)

      if (result.error) {
        toast.error(t('leadForm.errorTitle'), {
          description: result.error,
        })
      } else {
        toast.success(t('leadForm.successTitle'), {
          description: t('leadForm.successDescription'),
        })
        setIsSuccess(true)
      }
    } catch {
      toast.error(t('leadForm.errorTitle'), {
        description: t('leadForm.errorDescription'),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <Card className="w-full max-w-lg mx-auto border-primary/20 shadow-lg bg-white/90 backdrop-blur-sm">
        <CardContent className="pt-10 pb-10 text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-primary">{t('leadForm.thankYou')}</h3>
          <p className="text-muted-foreground max-w-xs mx-auto">
            {t('leadForm.thankYouDescription')}
          </p>
          <Button variant="outline" onClick={() => setIsSuccess(false)} className="mt-4">
            {t('leadForm.sendAnother')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-xl mx-auto border-border/50 shadow-xl bg-white">
      <CardHeader className="space-y-1 text-center pb-8">
        <CardTitle className="text-2xl font-bold text-primary">{t('leadForm.title')}</CardTitle>
        <CardDescription>{t('leadForm.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">{t('leadForm.fullName')}</Label>
              <Input
                id="fullName"
                placeholder={t('leadForm.placeholderName')}
                {...form.register('fullName')}
                className={form.formState.errors.fullName ? 'border-red-500' : ''}
              />
              {form.formState.errors.fullName && (
                <p className="text-xs text-red-500">{form.formState.errors.fullName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('leadForm.workEmail')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('leadForm.placeholderEmail')}
                {...form.register('email')}
                className={form.formState.errors.email ? 'border-red-500' : ''}
              />
              {form.formState.errors.email && (
                <p className="text-xs text-red-500">{form.formState.errors.email.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="linkedinUrl">{t('leadForm.linkedinUrl')}</Label>
            <Input
              id="linkedinUrl"
              placeholder={t('leadForm.placeholderLinkedin')}
              {...form.register('linkedinUrl')}
              className={form.formState.errors.linkedinUrl ? 'border-red-500' : ''}
            />
            {form.formState.errors.linkedinUrl && (
              <p className="text-xs text-red-500">{form.formState.errors.linkedinUrl.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="orgName">{t('leadForm.organizationName')}</Label>
              <Input
                id="orgName"
                placeholder={t('leadForm.placeholderOrg')}
                {...form.register('orgName')}
                className={form.formState.errors.orgName ? 'border-red-500' : ''}
              />
              {form.formState.errors.orgName && (
                <p className="text-xs text-red-500">{form.formState.errors.orgName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">{t('leadForm.yourRole')}</Label>
              <Input
                id="role"
                placeholder={t('leadForm.placeholderRole')}
                {...form.register('role')}
                className={form.formState.errors.role ? 'border-red-500' : ''}
              />
              {form.formState.errors.role && (
                <p className="text-xs text-red-500">{form.formState.errors.role.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="networkSize">{t('leadForm.networkSize')}</Label>
            <Select onValueChange={(val) => form.setValue('networkSize', val)}>
              <SelectTrigger>
                <SelectValue placeholder={t('leadForm.selectSize')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0-100">{t('leadForm.size0to100')}</SelectItem>
                <SelectItem value="101-1000">{t('leadForm.size101to1000')}</SelectItem>
                <SelectItem value="1001-2000">{t('leadForm.size1001to2000')}</SelectItem>
                <SelectItem value="2001-5000">{t('leadForm.size2001to5000')}</SelectItem>
                <SelectItem value="5001-7500">{t('leadForm.size5001to7500')}</SelectItem>
                <SelectItem value="7500+">{t('leadForm.size7500plus')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            type="submit"
            className="w-full text-lg py-6 font-bold mt-6"
            disabled={isSubmitting}
          >
            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
            {t('leadForm.requestFreeDemo')}
            {!isSubmitting && <ArrowRight className="w-5 h-5 ml-2" />}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
